'use client';

import { useEffect, useState } from 'react';
import { Mail, Star, Trash2 } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalConfirmStore } from '@/stores/useModalConfirmStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import LabelBadge from '@/components/LabelBadge';
import SectionTitle from '@/components/SectionTitle';
import DashboardPersonalNoteUnit from '@/components/features/dashboard/unit/DashboardPersonalNoteUnit';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import FormInput from '@/components/ui/form/FormInput';
import FormInputWithLabel from '@/components/ui/form/FormInputWithLabel';
import FormSectionTitle from '@/components/ui/form/FormSectionTitle';

interface DashboardPersonalSubEmailAddressAuthProps {
    editEmail: string | null;
}
function DashboardPersonalSubEmailAddressAuth({ editEmail }: DashboardPersonalSubEmailAddressAuthProps) {
    const { handleError, errorMessage, clearError } = useHandleError();
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const closeModal = useModalStore((state) => state.closeModal);
    const setUserRegistrationStatus = useAuthStore((s) => s.setUserRegistrationStatus);

    const handleAuthCodeInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length === 6) {
            try {
                clearError();
                await apiFetch(`/users/me/sub_email`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ code: value, sub_email: editEmail }),
                });
                closeModal();
                setUserRegistrationStatus({ sub_email: 1 });
                await showToastAsync('サブメールアドレスの設定が完了しました', 'success');
            } catch (err: unknown) {
                handleError(err);
            }
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="section-spacer-inner-y"></div>
            <div className="flex justify-start items-center w-full py-4">
                <FormSectionTitle title="認証コード入力" />
            </div>
            <div className="flex flex-col justify-start items-center w-full py-4">
                <div className="flex items-start justify-start w-full">
                    <ErrorBox message={errorMessage?.message} />
                </div>
                <div className="flex flex-col items-center w-full">
                    <div className="flex justify-start items-center w-full">
                        <FormFieldLabel title="新しいメールアドレス" />
                    </div>
                    <div className="my-2"></div>
                    <div className="flex w-full px-4 text-base">{editEmail}</div>
                </div>
                <div className="py-6 md:py-4"></div>
                <div className="flex flex-col justify-center items-center bg-color-white-base w-full h-full">
                    <div className="flex justify-start items-center w-full mb-4">
                        <FormFieldLabel title="認証コード" />
                        <LabelBadge type="required" />
                    </div>
                    <div className="flex flex-col items-start justify-start w-[90%] xl:w-[70%]">
                        <div id="code" className="flex flex-col items-center w-full">
                            <FormInput
                                id="code"
                                name="code"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="＊＊＊＊＊＊"
                                autoComplete="off"
                                autoFocus={true}
                                onChange={handleAuthCodeInputChange}
                                className="rounded-3xl text-center py-4"
                            />
                        </div>
                    </div>
                    <div className="section-spacer-inner-4-y"></div>
                </div>
            </div>
        </div>
    );
}
interface DashboardPersonalEmailAddressEditProps {
    goToAuth: () => void;
    currentEmail: string | null;
    setCurrentEmail: (email: string | null) => void;
    setEditEmail: (editEmail: string) => void;
}
function DashboardPersonalEmailAddressEdit({
    goToAuth,
    currentEmail,
    setCurrentEmail,
    setEditEmail,
}: DashboardPersonalEmailAddressEditProps) {
    const { handleError, errorMessage, clearError } = useHandleError();
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState({ sub_email: '' });
    const openModalConfirm = useModalConfirmStore((s) => s.openModalConfirm);
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const setUserRegistrationStatus = useAuthStore((s) => s.setUserRegistrationStatus);
    const setUserAuth = useAuthStore((s) => s.setUserAuth);
    const normalizeAndStripSpaces = (value: string) => {
        return value.normalize('NFKC').replace(/[\s\u3000]/g, '');
    };

    const handleAuthIdentifierBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
        const target = e.target as Element;
        if (!(target instanceof HTMLInputElement)) return;
        const value = normalizeAndStripSpaces(target.value);
        handleEmailInputChange(value);
    };

    const handleEmailInputChange = (value: string) => {
        setFormData({
            ...formData,
            sub_email: value,
        });
    };

    const handleSubmit = async () => {
        try {
            clearError();
            setIsProcessing(true);
            const response = await apiFetch(`/users/me/sub_email/update/activation/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            goToAuth();
            setEditEmail(response.sub_email);
        } catch (err: unknown) {
            handleError(err);
            setIsProcessing(false);
        }
    };

    const handleDelete = () => {
        openModalConfirm({
            id: 'delete-sub-email',
            message: 'サブメールアドレスを削除しますか？',
            confirmLabel: '削除する',
            cancelLabel: 'キャンセル',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    clearError();
                    await apiFetch(`/users/me/sub_email`, { method: 'DELETE' });
                    setCurrentEmail(null);
                    setUserRegistrationStatus({ sub_email: 0 });
                    await showToastAsync('サブメールアドレスを削除しました', 'success');
                } catch (err: unknown) {
                    handleError(err);
                }
            },
        });
    };

    const handleSetPrimary = () => {
        openModalConfirm({
            id: 'set-primary-sub-email',
            message: 'サブメールアドレスをメインに設定しますか？',
            subMessage: 'ログインに使うメールアドレスが変わります。現在のメインはサブに入れ替わります。',
            confirmLabel: 'メインに設定する',
            cancelLabel: 'キャンセル',
            variant: 'warning',
            onConfirm: async () => {
                try {
                    clearError();
                    const response = await apiFetch(`/users/me/sub_email/primary`, { method: 'POST' });
                    setCurrentEmail(response.sub_email || null);
                    setUserRegistrationStatus({ email: 1, sub_email: response.sub_email ? 1 : 0 });
                    const latestUserAuth = useAuthStore.getState().userAuth;
                    if (latestUserAuth) {
                        setUserAuth({ ...latestUserAuth, email: response.email });
                    }
                    await showToastAsync('メインのメールアドレスを変更しました', 'success');
                } catch (err: unknown) {
                    handleError(err);
                }
            },
        });
    };

    return (
        <>
            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="サブメールアドレス設定" />
                </div>
                <div className="flex w-full pt-2">
                    <DashboardPersonalNoteUnit
                        messages={[
                            '所属先の所有するライセンスを利用するために使うメールアドレスです。',
                            '学校や企業に所属されている方は、所属先のメールアドレスをご登録ください。',
                            '個人でご利用の方は登録不要です。',
                        ]}
                    />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div className="flex items-start justify-start w-full">
                        <ErrorBox message={errorMessage?.message} />
                    </div>
                    <div className="flex flex-col items-center w-full font-notojp">
                        <div className="flex justify-start items-center w-full">
                            <FormFieldLabel title="現在のメールアドレス" />
                        </div>
                        <div className="my-2"></div>
                        <div className="flex w-full px-4 text-sm md:text-base break-words break-all">
                            {currentEmail || 'サブメールアドレスは設定されていません'}
                        </div>
                        {currentEmail && (
                            <div className="flex flex-col md:flex-row justify-center items-center gap-4 w-full pt-6">
                                <ButtonGAdaptive
                                    label="メインに設定する"
                                    icon={Star}
                                    onClick={handleSetPrimary}
                                    colorType={1}
                                    size="xs"
                                />
                                <ButtonGAdaptive
                                    label="削除する"
                                    icon={Trash2}
                                    onClick={handleDelete}
                                    colorType={2}
                                    size="xs"
                                />
                            </div>
                        )}
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="sub_email" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full relative">
                            <FormFieldLabel title="新しいメールアドレス" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="sub_email" />
                        </div>
                        <div className="flex w-full" onBlur={handleAuthIdentifierBlur}>
                            <FormInputWithLabel
                                id="sub_email"
                                name="sub_email"
                                label="254文字以内"
                                type="email"
                                maxLength={254}
                                autoComplete="off"
                                value={formData.sub_email}
                                inputMode="email"
                                onChange={(e) => {
                                    handleEmailInputChange(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-start justify-start w-full py-2">
                <div className="flex flex-col-reverse md:flex-row justify-center w-full pt-12">
                    <div className="flex-grow"></div>
                    <div className="flex flex-col justify-end items-center">
                        <div className="flex justify-center items-center">
                            <ButtonGAdaptive
                                type="submit"
                                label="認証メールを送信する"
                                onClick={handleSubmit}
                                description="send verification email"
                                icon={Mail}
                                isProcessing={isProcessing}
                            />
                        </div>
                    </div>
                    <div className="flex-grow"></div>
                </div>
            </div>
        </>
    );
}

export default function DashboardPersonalSubEmailAddress() {
    const [viewState, setViewState] = useState<'edit' | 'auth'>('edit');
    const [editEmail, setEditEmail] = useState<string | null>(null);
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);
    const { handleError } = useHandleError();

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch(`/users/me/sub_email`);
                setCurrentEmail(data.sub_email || null);
            } catch (err: unknown) {
                handleError(err);
            }
        })();
    }, []);

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title={`Sub Email Address`} />
                </div>
            </div>

            {viewState === 'edit' && (
                <DashboardPersonalEmailAddressEdit
                    goToAuth={() => setViewState('auth')}
                    currentEmail={currentEmail}
                    setCurrentEmail={setCurrentEmail}
                    setEditEmail={setEditEmail}
                />
            )}
            {viewState === 'auth' && <DashboardPersonalSubEmailAddressAuth editEmail={editEmail} />}
        </div>
    );
}
